import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useCart } from "../../contexts/CartContext";
import api from "../../lib/api";
import CyberDayBanner from "../CyberDayBanner";
import Logo from "../Logo";

export default function PublicHeader() {
    const { user, logout } = useAuth();
    const { cart } = useCart();
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        api.get("/categories").then(({ data }) => setCategories((data || []).filter((c) => c.product_count > 0))).catch(() => {});
    }, []);

    return (
        <div className="sticky top-0 z-40">
            <CyberDayBanner />
            <header
                data-testid="public-header"
                className="backdrop-blur-xl bg-avx-bg/80 border-b border-avx-line"
            >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
                <Logo testId="logo-link" />
                <nav className="hidden md:flex items-center gap-7 text-sm">
                    <NavLink to="/" data-testid="nav-home" className={({ isActive }) => isActive ? "text-avx-blue" : "text-avx-text/80 hover:text-avx-blue transition-colors"}>Home</NavLink>
                    <div className="relative group" data-testid="nav-plugins-dropdown">
                        <NavLink to="/plugins" data-testid="nav-catalog" className={({ isActive }) => `inline-flex items-center gap-1 ${isActive ? "text-avx-blue" : "text-avx-text/80 hover:text-avx-blue transition-colors"}`}>
                            Plugins
                            {categories.length > 0 && (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3 h-3 mt-0.5 opacity-70"><path d="M6 9l6 6 6-6" /></svg>
                            )}
                        </NavLink>
                        {categories.length > 0 && (
                            <div className="absolute left-0 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                                <div className="w-60 panel-elevated p-2 shadow-2xl" data-testid="nav-category-menu">
                                    <Link to="/plugins" className="block px-3 py-2 rounded-sm text-xs font-mono uppercase tracking-[0.15em] text-avx-text/80 hover:bg-avx-blue/10 hover:text-avx-blue transition-colors">Todos los plugins</Link>
                                    <div className="my-1 border-t border-avx-line" />
                                    {categories.map((c) => (
                                        <Link
                                            key={c.id}
                                            to={`/plugins/categoria/${c.slug}`}
                                            data-testid={`nav-cat-${c.slug}`}
                                            className="flex items-center justify-between px-3 py-2 rounded-sm text-sm text-avx-text/85 hover:bg-avx-blue/10 hover:text-avx-blue transition-colors"
                                        >
                                            <span>{c.name}</span>
                                            <span className="text-[10px] font-mono text-avx-muted">{c.product_count}</span>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <NavLink to="/desktop" data-testid="nav-desktop" className={({ isActive }) => isActive ? "text-avx-blue" : "text-avx-text/80 hover:text-avx-blue transition-colors"}>AVX Central</NavLink>
                    <NavLink to="/artistas" data-testid="nav-artists" className={({ isActive }) => isActive ? "text-avx-blue" : "text-avx-text/80 hover:text-avx-blue transition-colors"}>Artists</NavLink>
                    <NavLink to="/benefits" className={({ isActive }) => `transition-colors ${isActive ? "text-avx-blue" : "text-avx-text/80 hover:text-avx-blue"}`} data-testid="nav-benefits">Benefits</NavLink>
                    <a href="/#faq" className="text-avx-text/80 hover:text-avx-blue transition-colors" data-testid="nav-faq">FAQ</a>
                </nav>
                <div className="flex items-center gap-2">
                    <Link
                        to="/cart"
                        aria-label="Cart"
                        data-testid="header-cart-link"
                        className="relative flex items-center justify-center w-10 h-10 rounded-sm border border-avx-line hover:border-avx-blue/60 text-avx-text/80 hover:text-avx-blue transition-colors"
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                            <path d="M3 3h2l2.4 12.3a2 2 0 0 0 2 1.7h8.2a2 2 0 0 0 2-1.6L21 8H6" />
                            <circle cx="9" cy="20" r="1.5" />
                            <circle cx="17" cy="20" r="1.5" />
                        </svg>
                        {cart.count > 0 && (
                            <span
                                data-testid="header-cart-badge"
                                className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-avx-blue text-avx-bg text-[10px] font-heading font-black flex items-center justify-center"
                            >
                                {cart.count}
                            </span>
                        )}
                    </Link>
                    {user ? (
                        <>
                            <Link
                                to={user.role === "admin" ? "/admin" : "/dashboard"}
                                className="btn-secondary px-4 py-2 text-xs font-mono uppercase tracking-[0.18em] rounded-sm"
                                data-testid="header-dashboard-btn"
                            >
                                My dashboard
                            </Link>
                            <button
                                onClick={async () => { await logout(); navigate("/"); }}
                                className="text-xs text-avx-muted hover:text-avx-danger uppercase tracking-[0.18em] font-mono px-3"
                                data-testid="header-logout-btn"
                            >
                                Log out
                            </button>
                        </>
                    ) : (
                        <>
                            <Link to="/login" className="text-xs font-mono uppercase tracking-[0.18em] text-avx-text/80 hover:text-avx-blue px-3" data-testid="header-login-link">
                                Log in
                            </Link>
                            <Link to="/plugins" className="btn-primary px-4 py-2 text-xs font-mono uppercase tracking-[0.18em] rounded-sm" data-testid="header-cta">
                                View plugins
                            </Link>
                        </>
                    )}
                </div>
            </div>
        </header>
        </div>
    );
}
